import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@tsuyosa/auth';
import type { AuthError } from '@tsuyosa/auth';

export function LoginPage() {
  const { login, resendVerification, user } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [isLoading, setLoading] = useState(false);

  const [notVerified, setNotVerified] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendMsg, setResendMsg] = useState('');

  if (user) { navigate('/'); return null; }

  function startCooldown() {
    setResendCooldown(60);
    const interval = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setNotVerified(false);
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      const authErr = err as AuthError;
      if (authErr.code === 'EMAIL_NOT_VERIFIED') {
        setNotVerified(true);
        startCooldown();
      } else {
        setError(authErr.message ?? 'Login gagal');
      }
    } finally { setLoading(false); }
  }

  async function handleResend() {
    setResendMsg('');
    try {
      await resendVerification(email);
      setResendMsg('Email verifikasi sudah dikirim ulang. Cek inbox kamu.');
      startCooldown();
    } catch (err) {
      setResendMsg(err instanceof Error ? err.message : 'Gagal mengirim ulang email');
    }
  }

  const inputClass =
    'w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder-white/30 outline-none focus:border-[#f472b6]/50 focus:ring-1 focus:ring-[#f472b6]/30';

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#0d0a12] px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-gradient-tsuyosa">TSUN</h1>
          <p className="mt-1 text-sm text-white/40">Tsuyosa Fan Portal</p>
        </div>

        <div className="rounded-xl border border-white/5 bg-[#130d1a] p-6">
          <h2 className="mb-5 text-lg font-semibold text-white">Masuk</h2>

          {notVerified && (
            <div className="mb-4 rounded-lg border border-amber-500/20 bg-amber-500/10 px-4 py-3">
              <p className="mb-2 text-sm text-amber-300">
                Email kamu belum diverifikasi. Cek inbox untuk link verifikasi.
              </p>
              {resendMsg && (
                <p className="mb-2 text-xs text-white/50">{resendMsg}</p>
              )}
              <button
                onClick={handleResend}
                disabled={resendCooldown > 0}
                className="text-xs font-medium text-amber-400 underline disabled:cursor-not-allowed disabled:opacity-50"
              >
                {resendCooldown > 0 ? `Kirim ulang (${resendCooldown}s)` : 'Kirim ulang email verifikasi'}
              </button>
            </div>
          )}

          {error && (
            <p className="mb-4 rounded-lg bg-red-500/10 px-4 py-3 text-sm text-red-400">
              {error}
            </p>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <label className="mb-1.5 block text-sm text-white/60">Email</label>
              <input
                type="email" required value={email} onChange={e => setEmail(e.target.value)}
                className={inputClass}
                placeholder="email@example.com"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm text-white/60">Password</label>
              <input
                type="password" required value={password} onChange={e => setPassword(e.target.value)}
                className={inputClass}
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit" disabled={isLoading}
              className="mt-1 flex w-full items-center justify-center rounded-lg bg-[#f472b6] py-2.5 text-sm font-semibold text-white shadow-[0_0_12px_rgba(244,114,182,0.35)] transition-all hover:bg-[#ec4899] hover:shadow-[0_0_20px_rgba(244,114,182,0.6)] disabled:opacity-50"
            >
              {isLoading
                ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                : 'Masuk'
              }
            </button>
          </form>
        </div>

        <p className="mt-4 text-center text-sm text-white/40">
          Belum punya akun?{' '}
          <Link to="/register" className="text-[#f472b6] hover:underline">
            Daftar sekarang
          </Link>
        </p>
      </div>
    </div>
  );
}
